import { useCallback } from 'react'
import { useAuth } from '../auth/useAuth'
import { auth } from '../../firebase'
import { apiFetch, type ApiFetchInit } from './apiFetch'
import { ApiError } from './ApiError'
import { activeDistrictStore } from '../auth/activeDistrictStore'

export function useApiFetch() {
  const { state, signOut } = useAuth()
  return useCallback(async function call<T>(path: string, init?: Omit<ApiFetchInit, 'token'>): Promise<T> {
    // Prefer the React auth state, but fall back to Firebase's authoritative
    // `auth.currentUser`. During the magic-link bind the request fires in the
    // same tick that signInWithEmailLink resolves: Firebase has already set
    // currentUser, but the AuthProvider's onAuthStateChanged listener has not
    // yet re-rendered React into 'signed-in'. Reading currentUser avoids a
    // spurious "Not signed in" that would otherwise abort the sign-in.
    const user = state.status === 'signed-in' ? state.user : auth.currentUser
    if (!user) throw new Error('Not signed in')

    const token = await user.getIdToken()
    const districtId = activeDistrictStore.snapshot()
    // A caller that names its district (a district write pinned to the district it was made
    // on) keeps it; everything else goes to the district the user has active.
    const headers: Record<string, string> = {
      ...(districtId ? { 'X-District-Id': districtId } : {}),
      ...init?.headers,
    }
    try {
      return await apiFetch<T>(path, { ...init, headers, token })
    } catch (err) {
      // A cached Firebase ID token can be stale on the first request after a
      // hard refresh (the session is restored before the token is refreshed).
      // The API rejects such a request with 401/403 ("no authenticated user
      // context"). Auth rejection happens before any handler side effect, so
      // retrying is safe even for non-idempotent methods. Retry once with a
      // force-refreshed token before surfacing the error to the UI.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        let freshToken: string
        try {
          freshToken = await user.getIdToken(true)
        } catch {
          // A forced token refresh only fails when the session itself is gone
          // (the refresh token was revoked, e.g. after a password change, or
          // has fully expired). This is distinct from an authorization 403
          // (missing district/admin rights), where getIdToken succeeds and the
          // retried request still fails — those we surface unchanged below.
          // The session is unrecoverable in-place, so sign the user out and
          // send them to re-login rather than showing a generic error.
          await redirectToLogin(signOut)
          throw err
        }
        return await apiFetch<T>(path, { ...init, headers, token: freshToken })
      }
      throw err
    }
  }, [state, signOut])
}

/**
 * Ends the dead session and routes the user to the login page, preserving the
 * current location in `next` so they land back where they were after
 * re-authenticating. A hard navigation (rather than a router push) guarantees
 * all in-memory app/query state tied to the old identity is discarded.
 */
async function redirectToLogin(signOut: () => Promise<void>): Promise<void> {
  try {
    await signOut()
  } catch {
    // Ignore — we redirect to login regardless of whether the local sign-out
    // bookkeeping succeeds.
  }
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.assign(`/login?next=${next}`)
}
